import { Anchor, Badge, Button, Group, Loader, Stack, Table, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconLink, IconRefresh } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { SenderLikeFragment } from "src/gql/graphql";

const TableRow = ({ type, subject, id }: { type: "draft" | "template"; subject: string; id: string }) => {
    return (
        <Table.Tr>
            <Table.Td>{type == "draft" ? <Badge color="red">Draft</Badge> : <Badge color="blue">Template</Badge>}</Table.Td>
            <Table.Td>
                <Text fz="sm">{subject}</Text>
            </Table.Td>
            <Table.Td>
                <Anchor c="var(--mantine-color-text)" target="_blank" href={`/messages/${type}s/${id}`} aria-label="message link">
                    <IconLink size={16} />
                </Anchor>
            </Table.Td>
        </Table.Tr>
    );
};

function useArchiveSender({ sender, onSuccess }: { sender: SenderLikeFragment; onSuccess?: () => void }) {
    const {
        data: {
            sender: { blockers },
        },
    } = useSuspenseQuery({
        queryKey: qk("senders", "id", sender.id, "blockers"),
        queryFn: () => httpPostGraphql(gql.blockers, { id: sender.id }),
    });

    const { mutateAsync, isPending } = useGraphqlMutation({
        document: gql.archiveSender,
        onSuccess: async () => {
            await Promise.all([qk.invalidate("senders", "list")]);
            notify.show.success({ message: "Sender archived" });
            await qk.invalidate("group");
        },
    });

    const Modal = () => {
        const {
            data: {
                sender: { blockers },
            },
            isRefetching,
        } = useSuspenseQuery({
            queryKey: qk("senders", "id", sender.id, "blockers"),
            queryFn: () => httpPostGraphql(gql.blockers, { id: sender.id }),
        });

        const rows = blockers.messages
            .map((it) => <TableRow type="draft" subject={it.subject} id={it.id} key={it.id} />)
            .concat(
                blockers.templates.map((it) => <TableRow type="template" subject={it.message.subject} id={it.id} key={it.message.id} />),
            );
        return (
            <Stack>
                <Text>Senders can not be deleted until they are removed from all draft messages and templates</Text>
                <Table striped>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Sender</Table.Th>
                            <Table.Th>Value</Table.Th>
                            <Table.Th w={1}>
                                <Button
                                    variant="default"
                                    rightSection={isRefetching ? <Loader color="white" size="sm" /> : <IconRefresh />}
                                    onClick={() => {
                                        qk.invalidate("senders", "id", sender.id).catch(notify.catch);
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{rows}</Table.Tbody>
                </Table>
                {!rows.length && (
                    <Group justify="center">
                        <Text fs="italic">None</Text>
                    </Group>
                )}
                <Group w="100%" justify="flex-end">
                    <Button
                        disabled={blockers.isBlocked}
                        color="red"
                        onClick={() =>
                            void mutateAsync({ senderId: sender.id })
                                .then(() => onSuccess?.())
                                .then(modals.closeAll)
                                .catch(notify.catch)
                        }
                    >
                        Archive
                    </Button>
                </Group>
            </Stack>
        );
    };

    const modalInfo = {
        title: "Archive Sender",
        children: (
            <Suspense fallback={<Loader />}>
                <Modal />
            </Suspense>
        ),
        onClose: modals.closeAll,
        size: "lg",
    };

    function archiveSender() {
        if (blockers.isBlocked) modals.open(modalInfo);
        else
            mutateAsync({ senderId: sender.id })
                .then(() => onSuccess?.())
                .catch(notify.catch);
    }

    return {
        archiveSender,
        archiveIsPending: isPending,
    };
}

export function useArchiveSenderModal({ sender, onSuccess }: { sender: SenderLikeFragment; onSuccess?: () => void }) {
    const { archiveSender, archiveIsPending } = useArchiveSender({ sender, onSuccess });

    function openArchiveModal() {
        modals.openConfirmModal({
            title: "Delete sender?",
            centered: true,
            children: <Text>Are you sure you want to permanently delete this sender? This action cannot be undone.</Text>,
            labels: { confirm: "Delete Sender", cancel: "Cancel" },
            onConfirm: archiveSender,
        });
    }

    return { openArchiveModal, archiveIsPending };
}

const gql = {
    blockers: graphql(`
        query SenderBlockers($id: UUID!) {
            sender(id: $id) {
                blockers {
                    isBlocked
                    messages {
                        subject
                        id
                    }
                    templates {
                        id
                        message {
                            subject
                            id
                        }
                    }
                }
            }
        }
    `),
    archiveSender: graphql(`
        mutation ArchiveSender($senderId: UUID!) {
            archiveSender(id: $senderId) {
                blockers {
                    isBlocked
                }
            }
        }
    `),
};
